import React, { createElement } from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'react-flexbox-grid';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { values, sum } from 'ramda';

import VerticalMiddler from 'components/VerticalMiddler';
import { dimensions } from 'styles/provider';
import exists from 'lib/exists';

import classes from './Tile.less';

/*
  OverlayText component
  Used to display an overlay text on an empty tile while loading or if
  the user has not inputted any data yet.
 */
const OverlayText = ({ contains }) => (
  <div className={classes.overlay}>
    <h2 className={classes.overlayText}>{contains}</h2>
  </div>
);

const EditingOverlay = routeTo => (
  <div className={classes.editingOverlay}>
    <p className={classes.editingButtonArea}>
      <FormattedMessage id="dictionary.continueEditing" />
    </p>
  </div>
);

OverlayText.propTypes = { contains: PropTypes.node };

const DisablableLink = ({ linkDisabled = false, ...other }) =>
  linkDisabled ? <span {...other} /> : <Link {...other} />;

DisablableLink.propTypes = { linkDisabled: PropTypes.bool };

const countIsZero = obj => sum(values(obj)) === 0;

/*
  Tile component
  Used to render tiles on the summary page and wrapped in SummaryLoaderContainer
  component.
 */
const Tile = ({
  icon,
  isLoading,
  data,
  count,
  view,
  localeMessage,
  localeId = 'dictionary.errNoLocId',
  routeTo = '',
  vendorLogo = null,
  alwaysRender = false,
  editing = false,
}) => {
  let routeToPage = routeTo;

  // Prevent onlineRehabilitation tile from rendering if theres no documents and testing mode not enabled
  if (
    !__ENABLE_PROD_TESTING__ &&
    routeToPage === '/documents/onlineRehabilitation' &&
    (!Array.isArray(data) || data.length === 0)
  ) {
    return <React.Fragment />;
  }
  // If HCP/Doctor viewing the patient, disable link to onlineRehabilitation page
  if (__ROLE__ === 'DOCTOR' && routeToPage === '/documents/onlineRehabilitation') {
    routeToPage = null;
  }

  return (
    <Col xs={12} sm={6} md={4}>
      <DisablableLink linkDisabled={!routeToPage} to={routeToPage} className={classes.tile}>
        <header className={classes.tileHeader}>
          <div className={classes.icon}>
            <img alt="" src={icon} />
          </div>
          <div className={classes.title}>
            {!vendorLogo && (
              <VerticalMiddler wrapperHeight={dimensions.tileHeaderHeight}>
                <FormattedMessage id={localeId} />
              </VerticalMiddler>
            )}
            {vendorLogo && (
              <Row middle="xs" between="xs" style={{ height: dimensions.tileHeaderHeight }}>
                <FormattedMessage id={localeId} />
                {vendorLogo && <img src={vendorLogo} />}
              </Row>
            )}
          </div>
        </header>
        <section className={classes.content}>
          {isLoading && <OverlayText contains={<FormattedMessage id="dictionary.loading" />} />}
          {!isLoading && countIsZero(count) && !alwaysRender && (
            <OverlayText contains={<FormattedMessage id="dictionary.noData" />} />
          )}
          {!isLoading &&
            (!countIsZero(count) || alwaysRender) &&
            exists(view) &&
            createElement(view, { data, count, localeMessage, editing })}
        </section>
        {!isLoading && editing && createElement(EditingOverlay, { routeToPage })}
      </DisablableLink>
    </Col>
  );
};

Tile.propTypes = {
  localeId: PropTypes.string.isRequired,
  icon: PropTypes.node,
  isLoading: PropTypes.bool,
  alwaysRender: PropTypes.bool,
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  count: PropTypes.object,
  vendorLogo: PropTypes.any,
  view: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  routeTo: PropTypes.string,
  localeMessage: PropTypes.func.isRequired,
  editing: PropTypes.bool,
};

export default Tile;
